import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError, unwrap } from "../api.ts";
import { useBrand } from "../hooks.ts";
import { readSse } from "../lib/sse.ts";
import { Button, Card, ErrorText, Input, PageHeader, Spinner } from "../components/ui.tsx";

type LiveInput = Parameters<typeof api.me.browser.input.$post>[0]["json"]["events"][number];
type Phase = "start" | "opening" | "open" | "closed" | "done";

/** Keys sent as keys; everything else is typed as text. */
const KEYS = new Set(["Enter", "Backspace", "Tab", "Escape", "Delete", "ArrowLeft", "ArrowUp", "ArrowRight", "ArrowDown", "Home", "End", "PageUp", "PageDown"]);
/** The keyboard catcher always holds this one character, so a phone's backspace has something to delete. */
const SENTINEL = " ";

/**
 * The assistant's browser on the person's screen: they sign in to a site themselves (password, one-time code,
 * CAPTCHA) and the assistant carries on signed in. Opened from the link the assistant sends (?request=…) or
 * from the menu, to sign in somewhere ahead of time.
 */
export function BrowserPage() {
  const brand = useBrand();
  const [params] = useSearchParams();
  const requestId = params.get("request") ?? undefined;
  const request = useQuery({
    queryKey: ["browser-request", requestId],
    queryFn: () => unwrap(api.me.browser.requests[":id"].$get({ param: { id: requestId! } })),
    enabled: !!requestId,
    retry: false,
  });

  const [phase, setPhase] = useState<Phase>("start");
  const [error, setError] = useState<unknown>(null);
  const [closedReason, setClosedReason] = useState("");
  const [frame, setFrame] = useState<{ src: string; width: number; height: number } | null>(null);
  const [page, setPage] = useState({ url: "", title: "" });
  const [address, setAddress] = useState("");
  const [editing, setEditing] = useState(false);
  const [resumed, setResumed] = useState(false);
  const streamAbort = useRef<AbortController | null>(null);

  /* Typing and clicks are queued and sent in small batches, one request at a time, in order. */
  const queue = useRef<LiveInput[]>([]);
  const sending = useRef(false);
  const flush = useCallback(async () => {
    if (sending.current || !queue.current.length) return;
    sending.current = true;
    try {
      while (queue.current.length) {
        const events = queue.current.splice(0, 100);
        await unwrap(api.me.browser.input.$post({ json: { events } })).catch((e: unknown) => {
          if (e instanceof ApiError && e.status === 404) setPhase((p) => (p === "open" ? "closed" : p));
          else setError(e);
        });
      }
    } finally {
      sending.current = false;
    }
  }, []);
  const push = useCallback(
    (e: LiveInput) => {
      // A newer pointer position replaces one that hasn't gone yet.
      const last = queue.current[queue.current.length - 1];
      if (e.t === "mouse" && e.type === "mouseMoved" && last?.t === "mouse" && last.type === "mouseMoved") queue.current[queue.current.length - 1] = e;
      else queue.current.push(e);
      setTimeout(() => void flush(), 15);
    },
    [flush],
  );

  const follow = useCallback(async () => {
    streamAbort.current?.abort();
    const ac = new AbortController();
    streamAbort.current = ac;
    for (let attempt = 0; attempt < 5 && !ac.signal.aborted; attempt++) {
      try {
        const r = await fetch("/api/me/browser/stream", { signal: ac.signal, credentials: "same-origin" });
        if (r.status === 404) {
          setPhase((p) => (p === "done" ? p : "closed"));
          return;
        }
        if (!r.ok || !r.body) throw new ApiError(r.status, ((await r.json().catch(() => ({}))) as { error?: string }).error ?? r.statusText);
        for await (const { event, data } of readSse(r.body)) {
          attempt = 0;
          if (event === "frame") setFrame({ src: `data:image/jpeg;base64,${String(data.data)}`, width: Number(data.width), height: Number(data.height) });
          else if (event === "page") setPage({ url: String(data.url ?? ""), title: String(data.title ?? "") });
          else if (event === "closed") {
            setClosedReason(String(data.reason ?? ""));
            setPhase((p) => (p === "done" ? p : "closed"));
            return;
          }
        }
      } catch (e) {
        if (ac.signal.aborted) return;
        if (e instanceof ApiError && e.status < 500) return setError(e);
      }
      // The connection dropped (a phone locking, a network blip): the window is still open, so pick it up again.
      await new Promise((ok) => setTimeout(ok, 1000 * (attempt + 1)));
    }
  }, []);
  useEffect(() => () => streamAbort.current?.abort(), []);

  const open = useCallback(
    async (url?: string) => {
      setError(null);
      setPhase("opening");
      try {
        // On a phone the page gets a phone-sized screen, so it's readable without zooming.
        const phone = window.matchMedia("(max-width: 640px)").matches;
        const r = await unwrap(api.me.browser.open.$post({ json: { ...(requestId && !url ? { request: requestId } : { url }), phone } }));
        setPage(r.page);
        setFrame(null);
        setPhase("open");
        void follow();
      } catch (e) {
        setError(e);
        setPhase("start");
      }
    },
    [follow, requestId],
  );

  // The assistant's link opens the window straight away.
  const autoOpened = useRef(false);
  useEffect(() => {
    if (requestId && request.data && !autoOpened.current) {
      autoOpened.current = true;
      void open();
    }
  }, [open, request.data, requestId]);

  const finish = async () => {
    streamAbort.current?.abort();
    try {
      const r = await unwrap(api.me.browser.done.$post({ json: { request: request.data ? requestId : undefined } }));
      setResumed(r.resumed);
      setPhase("done");
    } catch (e) {
      setError(e);
    }
  };

  const go = (e: FormEvent) => {
    e.preventDefault();
    const v = address.trim();
    if (!v) return;
    const url = /^https?:\/\//i.test(v) ? v : `https://${v}`;
    setEditing(false);
    if (phase === "open") push({ t: "go", url });
    else void open(url);
  };

  if (requestId && request.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;

  const header = (
    <PageHeader
      title="Browser"
      description={`Your assistant's web browser, on your screen. Sign in to a website here and your assistant uses it signed in. What you type goes to the website, never to your assistant or ${brand.name}, so your password stays private.`}
    />
  );

  if (phase === "done") {
    return (
      <>
        {header}
        <Card className="px-5 py-8 text-center">
          <div className="text-base font-medium">Done — thanks.</div>
          <p className="mt-1 text-sm text-[var(--muted)]">
            {resumed ? "Your assistant has been told and is carrying on where it left off." : "The website stays signed in in your assistant's browser until the site signs you out."}
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <Link to="/chat"><Button variant="primary">Back to chat</Button></Link>
          </div>
        </Card>
      </>
    );
  }

  return (
    <>
      {header}
      {request.error && <Card className="mb-4 px-4 py-3 text-sm"><ErrorText error={request.error} /></Card>}
      {request.data && (
        <Card className="mb-4 px-4 py-3 text-sm">
          Your assistant needs you to sign in to <span className="font-medium">{request.data.site}</span> so it can {request.data.why.replace(/^[A-Z]/, (c) => c.toLowerCase())}.
          {" "}Sign in below — including any one-time code or CAPTCHA — then press <span className="font-medium">Done</span>.
        </Card>
      )}
      {error != null && <div className="mb-3"><ErrorText error={error} /></div>}

      {phase === "start" || phase === "closed" ? (
        <Card className="px-5 py-6">
          {phase === "closed" && <p className="mb-3 text-sm text-[var(--muted)]">{closedReason || "The browser window closed."}</p>}
          <form onSubmit={go} className="flex flex-col gap-2 sm:flex-row">
            <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Website to sign in to, e.g. example.com/login" inputMode="url" autoCapitalize="off" autoCorrect="off" />
            <Button variant="primary" type="submit" disabled={!address.trim()}>Open</Button>
            {request.data && <Button type="button" onClick={() => void open()}>Open the sign-in page again</Button>}
          </form>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="flex flex-wrap items-center gap-1.5 border-b border-[var(--line)] px-2 py-2">
            <Button variant="ghost" className="px-2.5" title="Back" aria-label="Back" onClick={() => push({ t: "nav", action: "back" })}>←</Button>
            <Button variant="ghost" className="px-2.5" title="Forward" aria-label="Forward" onClick={() => push({ t: "nav", action: "forward" })}>→</Button>
            <Button variant="ghost" className="px-2.5" title="Reload" aria-label="Reload" onClick={() => push({ t: "nav", action: "reload" })}>↻</Button>
            <form onSubmit={go} className="min-w-0 flex-1 basis-48">
              <Input
                value={editing ? address : page.url}
                onFocus={() => (setEditing(true), setAddress(page.url))}
                onBlur={() => setEditing(false)}
                onChange={(e) => setAddress(e.target.value)}
                aria-label="Address"
                inputMode="url"
                autoCapitalize="off"
                autoCorrect="off"
                className="font-mono text-xs"
              />
            </form>
            <Button variant="primary" onClick={() => void finish()}>{request.data ? "Done — I've signed in" : "Done"}</Button>
          </div>
          <Screen frame={frame} push={push} />
        </Card>
      )}
    </>
  );
}

/** The picture of the page, taking the person's clicks, scrolling and typing. */
function Screen({ frame, push }: { frame: { src: string; width: number; height: number } | null; push: (e: LiveInput) => void }) {
  const img = useRef<HTMLImageElement>(null);
  const kb = useRef<HTMLTextAreaElement>(null);
  const lastMove = useRef(0);
  const touchY = useRef<number | null>(null);
  const composing = useRef(false);

  const pos = (clientX: number, clientY: number) => {
    const r = img.current!.getBoundingClientRect();
    const clamp = (v: number) => Math.min(1, Math.max(0, v));
    return { fx: clamp((clientX - r.left) / r.width), fy: clamp((clientY - r.top) / r.height) };
  };
  const focusKeyboard = () => {
    const el = kb.current;
    if (!el) return;
    el.value = SENTINEL;
    el.focus({ preventScroll: true });
  };

  // Wheel scrolling must not also scroll this page, which React's (passive) onWheel can't prevent.
  useEffect(() => {
    const el = img.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      push({ t: "wheel", ...pos(e.clientX, e.clientY), dx: Math.max(-5000, Math.min(5000, e.deltaX)), dy: Math.max(-5000, Math.min(5000, e.deltaY)) });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  });

  const typed = (text: string) => {
    // A line break from a phone keyboard is the Enter key.
    text.split("\n").forEach((part, i) => {
      if (i > 0) push({ t: "key", key: "Enter" });
      if (part) push({ t: "text", text: part });
    });
  };

  return (
    <div className="relative bg-[var(--panel-2)]">
      {frame ? (
        <img
          ref={img}
          src={frame.src}
          alt="Your assistant's browser"
          draggable={false}
          className="mx-auto block w-full cursor-default select-none"
          style={{ aspectRatio: `${frame.width} / ${frame.height}`, maxWidth: frame.width < 700 ? frame.width : undefined, touchAction: "none" }}
          onContextMenu={(e) => e.preventDefault()}
          onMouseDown={(e) => {
            e.preventDefault();
            focusKeyboard();
            push({ t: "mouse", type: "mousePressed", ...pos(e.clientX, e.clientY), clicks: Math.min(3, Math.max(1, e.detail || 1)) });
          }}
          onMouseUp={(e) => push({ t: "mouse", type: "mouseReleased", ...pos(e.clientX, e.clientY), clicks: Math.min(3, Math.max(1, e.detail || 1)) })}
          onMouseMove={(e) => {
            if (Date.now() - lastMove.current < 60) return;
            lastMove.current = Date.now();
            push({ t: "mouse", type: "mouseMoved", ...pos(e.clientX, e.clientY) });
          }}
          onTouchStart={(e) => (touchY.current = e.touches.length === 1 ? e.touches[0]!.clientY : null)}
          onTouchMove={(e) => {
            const t = e.touches[0];
            if (touchY.current == null || !t) return;
            const dy = touchY.current - t.clientY;
            if (Math.abs(dy) < 4) return;
            touchY.current = t.clientY;
            // Scale the finger's movement up to the page's size.
            const scale = frame.width / (img.current?.getBoundingClientRect().width || frame.width);
            push({ t: "wheel", ...pos(t.clientX, t.clientY), dx: 0, dy: Math.round(dy * scale) });
          }}
        />
      ) : (
        <div className="grid aspect-[16/10] place-items-center"><Spinner /></div>
      )}
      <textarea
        ref={kb}
        aria-label="Type into the page"
        className="pointer-events-none absolute left-0 top-0 h-px w-px opacity-0"
        autoCapitalize="off"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        onKeyDown={(e) => {
          if (composing.current) return;
          if (KEYS.has(e.key) && !e.metaKey && !e.ctrlKey && !e.altKey) {
            e.preventDefault();
            push({ t: "key", key: e.key as Extract<LiveInput, { t: "key" }>["key"], shift: e.shiftKey });
          }
        }}
        onCompositionStart={() => (composing.current = true)}
        onCompositionEnd={(e) => {
          composing.current = false;
          if (e.data) typed(e.data);
          e.currentTarget.value = SENTINEL;
        }}
        onInput={(e) => {
          if (composing.current) return;
          const el = e.currentTarget;
          // The sentinel gone = a backspace (phones don't report the key itself).
          if (el.value === "") push({ t: "key", key: "Backspace" });
          else {
            const text = el.value.startsWith(SENTINEL) ? el.value.slice(SENTINEL.length) : el.value;
            if (text) typed(text);
          }
          el.value = SENTINEL;
        }}
      />
      <p className="border-t border-[var(--line)] px-3 py-2 text-xs text-[var(--muted)]">
        Click the page, then type. Pasting works too (for a password from your password manager). The window closes by itself after 30 minutes.
      </p>
    </div>
  );
}
